'use client'

import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import SectionHeader from '@/components/ui/SectionHeader'
import { Media } from '@/components/Media'
import { getLinkHref } from '@/components/Link'
import { cn } from '@/utilities/ui'
import type { Partner } from '@/payload-types'

// Dynamically import Splide to reduce initial bundle size
const Splide = dynamic(
  () =>
    import('@splidejs/react-splide').then((mod: any) => ({
      default: mod.Splide,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center h-32">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    ),
  },
)

const SplideSlide = dynamic(
  () =>
    import('@splidejs/react-splide').then((mod: any) => ({
      default: mod.SplideSlide,
    })),
  { ssr: false },
)

interface PartnersClientProps {
  header: {
    infoTagText?: string | null
    title: string
    subtitle?: string | null
  }
  partners: Partner[]
  background?: 'white' | 'light-gray' | 'dark'
}

const gradientClasses: Record<NonNullable<PartnersClientProps['background']>, { from: string; to: string }> = {
  white: { from: 'from-background', to: 'to-background/0' },
  'light-gray': { from: 'from-light-gray', to: 'to-light-gray/0' },
  dark: { from: 'from-dark', to: 'to-dark/0' },
}

export default function PartnersClient({ header, partners, background = 'white' }: PartnersClientProps) {
  const { from: gradientFrom, to: gradientTo } = gradientClasses[background]
  const [splideLoaded, setSplideLoaded] = useState(false)
  const [autoScrollExtension, setAutoScrollExtension] = useState<any>(null)

  // Dynamically load Splide CSS and AutoScroll extension
  useEffect(() => {
    const loadSplide = async () => {
      try {
        // Load Splide CSS dynamically
        if (!document.querySelector('link[href*="splide"]')) {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css'
          document.head.appendChild(link)
        }

        // Load AutoScroll extension
        const AutoScrollModule = await import('@splidejs/splide-extension-auto-scroll')
        setAutoScrollExtension({ AutoScroll: AutoScrollModule.AutoScroll })
        setSplideLoaded(true)
      } catch (error) {
        console.warn('Failed to load Splide:', error)
        setSplideLoaded(true) // Still render even if extension fails
      }
    }

    loadSplide()
  }, [])


  // Duplicate partners for seamless infinite loop
  const duplicatedPartners = [...partners, ...partners]

  return (
    <>
      <SectionHeader
        infoTagText={header.infoTagText ?? ''}
        title={header.title}
        subtitle={header.subtitle ?? undefined}
        align="center"
        size="md"
        className="mb-8"
      />

      <div className="relative overflow-hidden">
        {splideLoaded ? (
          // @ts-expect-error - @splidejs/react-splide has type definition issues with dynamic imports
          <Splide
            options={{
              type: 'loop',
              autoScroll: {
                pauseOnHover: false,
                pauseOnFocus: false,
                rewind: true,
                speed: 0.5,
              },
              arrows: false,
              pagination: false,
              fixedWidth: '200px',
              gap: '6rem',
              focus: 'center',
              perPage: 'auto',
              breakpoints: {
                768: {
                  fixedWidth: '160px',
                  gap: '4rem',
                },
                480: {
                  fixedWidth: '140px',
                  gap: '3rem',
                },
              },
            }}
            extensions={autoScrollExtension}
            className="partners-carousel"
          >
            {duplicatedPartners.map((partner, index) => {
              const lightLogo = typeof partner.logo === 'object' ? partner.logo : null
              const darkLogo =
                typeof partner.darkModeLogo === 'object' ? partner.darkModeLogo : null
              const hasDark = Boolean(darkLogo)
              const linkUrl = getLinkHref(partner.link) || '#'
              const openInNewTab = partner.link?.openInNewTab || false

              return (
                // @ts-expect-error - @splidejs/react-splide has type definition issues with dynamic imports
                <SplideSlide key={`${partner.name}-${index}`}>
                  <a
                    href={linkUrl}
                    {...(openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
                    className="flex items-center justify-center h-[100px]"
                  >
                    {lightLogo && (
                      <Media
                        resource={lightLogo}
                        alt={lightLogo.alt || `${partner.name} Logo`}
                        className={cn(
                          'flex h-full w-full items-center justify-center',
                          hasDark && 'dark:hidden',
                        )}
                        pictureClassName="block"
                        imgClassName="max-h-[100px] w-auto h-auto object-contain"
                        size="200px"
                        disableBlur
                      />
                    )}
                    {darkLogo && (
                      <Media
                        resource={darkLogo}
                        alt={darkLogo.alt || `${partner.name} Logo`}
                        className="hidden h-full w-full items-center justify-center dark:flex"
                        pictureClassName="block"
                        imgClassName="max-h-[100px] w-auto h-auto object-contain"
                        size="200px"
                        disableBlur
                      />
                    )}
                  </a>
                </SplideSlide>
              )
            })}
          </Splide>
        ) : (
          <ul
            className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 md:gap-x-16 py-4"
            aria-label="Partner"
          >
            {partners.map((partner) => {
              const lightLogo = typeof partner.logo === 'object' ? partner.logo : null
              const darkLogo =
                typeof partner.darkModeLogo === 'object' ? partner.darkModeLogo : null
              const hasDark = Boolean(darkLogo)
              const linkUrl = getLinkHref(partner.link) || '#'
              const openInNewTab = partner.link?.openInNewTab || false

              return (
                <li key={partner.id || partner.name} className="flex">
                  <a
                    href={linkUrl}
                    {...(openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
                    className="flex items-center justify-center h-[100px] w-[200px] max-w-[200px]"
                    aria-label={partner.name}
                  >
                    {lightLogo && (
                      <Media
                        resource={lightLogo}
                        alt={lightLogo.alt || `${partner.name} Logo`}
                        className={cn(
                          'flex h-full w-full items-center justify-center',
                          hasDark && 'dark:hidden',
                        )}
                        pictureClassName="block"
                        imgClassName="max-h-[100px] w-auto h-auto object-contain"
                        size="200px"
                        disableBlur
                      />
                    )}
                    {darkLogo && (
                      <Media
                        resource={darkLogo}
                        alt={darkLogo.alt || `${partner.name} Logo`}
                        className="hidden h-full w-full items-center justify-center dark:flex"
                        pictureClassName="block"
                        imgClassName="max-h-[100px] w-auto h-auto object-contain"
                        size="200px"
                        disableBlur
                      />
                    )}
                  </a>
                </li>
              )
            })}
          </ul>
        )}

        {/* Gradient overlays for smooth fade effect */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r ${gradientFrom} ${gradientTo} pointer-events-none z-10`}
        />
        <div
          className={`absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l ${gradientFrom} ${gradientTo} pointer-events-none z-10`}
        />
      </div>
    </>
  )
}
